import { get } from "lodash";
import { EyeIcon, PencilIcon, ClockIcon } from "@heroicons/react/outline";
import { ClubType, PagingType } from "routes/club/types";
import { isHaveActionPermission } from "utills/helpers";
import { useIsMobile } from "routes/club/hooks/useIsMobile";
import { TooltipParagraph } from "components/tooltipParagraph";
import { SwitchButton } from "components/switchButton";
import Button from "components/button";
import Pagination from "components/pagination";
import { ConfirmModal } from "components/confirmModal";
import { useState } from "react";
import { Tag } from "components/tag";
import { useUpdateStatus } from "routes/club/hooks/useUpdateStatus";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";

interface PropType {
  data: ClubType[];
  paging: PagingType | null;
  handleChangePage: (page: number) => void;
  handleUpdateTarget: (value: ClubType | null) => void;
  handleOpenFormModal: () => void;
  handleOpenLogHistoryModal: () => void;
  handleRefetchClubList: () => void;
}

const DataTable = (props: PropType) => {
  const {
    data,
    paging,
    handleChangePage,
    handleUpdateTarget,
    handleOpenFormModal,
    handleOpenLogHistoryModal,
    handleRefetchClubList,
  } = props;

  const isMobile = useIsMobile(1024);

  const { loading: statusLoading, mutation: updateStatusMutation } =
    useUpdateStatus();

  const [target, setTarget] = useState<ClubType | null>(null);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);

  const handleOpenConfirmModal = (item: ClubType) => {
    setTarget(item);
    setOpenConfirmModal(true);
  };

  const handleCloseModal = () => {
    setTarget(null);
    setOpenConfirmModal(false);
  };

  const handleConfirmStatus = async () => {
    console.log(target);

    if (!target) {
      toast.error("Club not found");
      return;
    }

    const res = await updateStatusMutation(
      target?.id ?? "",
      !target?.is_active
    );

    if (res.success) {
      toast.success("Update club status successfully");
      handleCloseModal();
      handleRefetchClubList();
    }
  };

  const handleUpdateClub = (value: ClubType) => {
    handleUpdateTarget(value);
    handleOpenFormModal();
  };

  const handleViewClubHistory = (value: ClubType) => {
    handleUpdateTarget(value);
    handleOpenLogHistoryModal();
  };

  return (
    <section className="box-container flex flex-col gap-3">
      {statusLoading && <FullScreenLoading />}

      <ConfirmModal
        key="confirm-change-status"
        title={"Confirm"}
        content={`Are you sure you want to activate this club?`}
        open={openConfirmModal}
        onConfirm={handleConfirmStatus}
        onClose={() => setOpenConfirmModal(false)}
      />

      {isMobile ? (
        <div className="mb-6 w-full flex flex-col gap-[8px]">
          {data?.map((item: ClubType, index: number) => {
            return (
              <div
                key={index}
                className="w-full bg-white p-[16px] rounded-[8px] shadow-md"
              >
                <div className="w-full flex items-center justify-between">
                  <SwitchButton
                    checked={get(item, "is_active", false)}
                    onChange={(value) => {
                      console.log(value);
                      handleOpenConfirmModal(item);
                    }}
                  />

                  <div className="flex items-center gap-2">
                    {isHaveActionPermission("club", "other", "edit") && (
                      <Button
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                        onClick={() => {
                          handleUpdateClub(item);
                        }}
                      >
                        <PencilIcon className="w-4 h-4 text-black" />
                      </Button>
                    )}

                    {isHaveActionPermission("club", "other", "view") && (
                      <Button
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                        onClick={() => {
                          handleViewClubHistory(item);
                        }}
                      >
                        <ClockIcon className="w-4 h-4 text-black" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="w-full mt-[16px] flex flex-col gap-[8px]">
                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      No
                    </span>
                    {/* <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {(get(paging, "current_page", 1) - 1) * PAGE_SIZE +
                        1 +
                        index}
                    </span> */}
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "no", 0)}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Status
                    </span>

                    <div className="w-full flex justify-end">
                      <Tag
                        text={
                          get(item, "is_active", false) ? "Active" : "Inactive"
                        }
                        type={get(item, "is_active", false) ? "green" : "pink"}
                      />
                    </div>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Club Name (VIE)
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right break-words">
                      {get(item, "label_vi", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Club Name (ENG)
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right break-words">
                      {get(item, "label_en", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Key
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right break-words">
                      {get(item, "key", "")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flow-root">
          <div className="overflow-x-auto min-w-full">
            <table className="mb-6 w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
                  >
                    No
                  </th>

                  <th scope="col" className="th">
                    Club Name (VIE)
                  </th>

                  <th scope="col" className="th">
                    Club Name (ENG)
                  </th>

                  <th scope="col" className="th">
                    Key
                  </th>

                  <th scope="col" className="th">
                    Status
                  </th>

                  <th scope="col" className="th w-28">
                    <span className="sr-only">Action</span>
                    <p className="">Action</p>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {data?.map((item: ClubType, index: number) => (
                  <tr key={index}>
                    {/* <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                  {(get(paging, "current_page", 1) - 1) * PAGE_SIZE + 1 + index}
                </td> */}

                    <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                      {get(item, "no", 0)}
                    </td>

                    <td className="td min-w-[150px] break-words">
                      {get(item, "label_vi", "")}
                    </td>

                    <td className="td min-w-[150px] break-words">
                      {get(item, "label_en", "")}
                    </td>

                    <td className="td max-w-[200px] break-words">
                      {get(item, "key", "")}
                    </td>

                    <td className="td">
                      <SwitchButton
                        disabled={
                          !isHaveActionPermission("club", "other", "edit")
                        }
                        checked={get(item, "is_active", false)}
                        onChange={(value) => {
                          handleOpenConfirmModal(item);
                        }}
                      />
                    </td>

                    <td className="td py-4 pl-3 pr-4 sm:pr-6 lg:pr-8 flex items-center gap-2">
                      {isHaveActionPermission("club", "other", "edit") && (
                        <Button
                          className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                          onClick={() => {
                            handleUpdateClub(item);
                          }}
                        >
                          <PencilIcon className="w-4 h-4 text-black" />
                        </Button>
                      )}

                      {isHaveActionPermission("club", "other", "view") && (
                        <Button
                          className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                          onClick={() => {
                            handleViewClubHistory(item);
                          }}
                        >
                          <ClockIcon className="w-4 h-4 text-black" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination
        currentPage={get(paging, "current_page", 1)}
        totalPage={get(paging, "total_page", 0)}
        total={{
          totalItems: get(paging, "total_item", 0),
          total: get(paging, "total", 0),
        }}
        onChange={handleChangePage}
      />
    </section>
  );
};

export default DataTable;
